import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  UiArguments,
} from "shared/provider-webworker";

import { AccountLookupService } from "./AccountLookupService";
import { ReplicaLookupService } from "../../impl/ReplicaLookupService";
import { IClientPromiseLookupService } from "../IClientPromiseLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";
import { AutoCompleteLabelValueReplicaLookupService } from "./AutoCompleteLabelValueReplicaLookupService";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

const TRADING_CHANNEL_PROFILE_VALUE: CubiqTS.ConcreteEncodable = CubiqTS.ENCODABLE(
  CubiqTS.TradingChannelProfileImpl,
);
const CLIENT_CONTACT_VALUE: CubiqTS.ConcreteEncodable = CubiqTS.ENCODABLE(
  CubiqTS.ClientContactImpl,
);
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class ClientReplicaLookupService
  extends AutoCompleteLabelValueReplicaLookupService<string, CubiqTS.ClientImpl>
  implements IClientPromiseLookupService
{
  constructor(protected factory: ClientMessageBrokerFactory) {
    super(factory, CubiqTS.ClientImpl, "ClientImpl");
  }

  getClient(id: string, type?: ClientAccountVisibilityType | null): Promise<CubiqTS.ClientImpl> {
    let request = new UiArguments("getClient");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1, new FnArg(type, PRIMITIVE)];
    return this.broker.runOnService(request, this.VALUE);
  }

  /*     getBranchCodes(): Promise<string[]> {
            let request = new UiArguments('getBranchCodes');
            request.args = [];
            return this.broker.runOnService(request, PRIMITIVE);
        } */

  getTradingChannels(): Promise<CubiqTS.TradingChannelProfileImpl[]> {
    let request = new UiArguments("getTradingChannels");
    request.args = [];
    return this.broker.runOnService(request, TRADING_CHANNEL_PROFILE_VALUE);
  }

  getRateSrc(): Promise<CubiqTS.RateSourceImpl[]> {
    let request = new UiArguments("getRateSrc");
    request.args = [];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  lookupClient(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupClient");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupClientsForId(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupClientsForId");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupClientsForName(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupClientsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  getClientContacts(id: string): Promise<CubiqTS.ClientContact[]> {
    let request = new UiArguments("getClientContacts");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, CLIENT_CONTACT_VALUE);
  }

  getClientContact(id: string): Promise<CubiqTS.ClientContact> {
    let request = new UiArguments("getClientContact");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, CLIENT_CONTACT_VALUE);
  }

  /* Active Clients only */
  getActiveClient(
    id: string,
    type?: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.ClientImpl> {
    let request = new UiArguments("getActiveClient");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1, new FnArg(type, PRIMITIVE)];
    return this.broker.runOnService(request, this.VALUE);
  }

  getActiveClientContacts(id: string): Promise<CubiqTS.ClientContact[]> {
    let request = new UiArguments("getActiveClientContacts");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, CLIENT_CONTACT_VALUE);
  }

  lookupActiveClientsForId(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupActiveClientsForId");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupActiveClientsForName(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupActiveClientsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAFillClientsForId(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupAFillClientsForId");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAFillClientsForName(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    let request = new UiArguments("lookupAFillClientsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupBMarkClientsForId(id: string, maxResults: number): Promise<CubiqTS.ClientImpl[]> {
    let request = new UiArguments("lookupBMarkClientsForId");
    let arg1 = new FnArg(id, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }
  lookupBMarkClientsForName(namePrefix: string, maxResults: number): Promise<CubiqTS.ClientImpl[]> {
    let request = new UiArguments("lookupBMarkClientsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }
}
